import {
  isSupportedCountryCode,
  normalizeCountryCode,
} from "@optrixis/config/geo";

export const PRICING_CATALOG_VERSION = "pricing-catalog.2026-09.v1";

export const standardPlanCodes = ["START", "GROWTH", "PRO"] as const;
export type StandardPlanCode = (typeof standardPlanCodes)[number];

export const standardBillingIntervals = ["MONTHLY", "ANNUAL"] as const;
export type StandardBillingInterval = (typeof standardBillingIntervals)[number];

export type BillingMarket = "MA" | "INTL";
export type BillingCurrency = "MAD" | "USD";

export interface PricingCatalogPrice {
  planCode: StandardPlanCode;
  market: BillingMarket;
  currency: BillingCurrency;
  billingInterval: StandardBillingInterval;
  amountMinor: number;
}

export const pricingCatalogPrices: readonly PricingCatalogPrice[] = [
  { planCode: "START", market: "MA", currency: "MAD", billingInterval: "MONTHLY", amountMinor: 24_900 },
  { planCode: "START", market: "MA", currency: "MAD", billingInterval: "ANNUAL", amountMinor: 249_000 },
  { planCode: "GROWTH", market: "MA", currency: "MAD", billingInterval: "MONTHLY", amountMinor: 39_900 },
  { planCode: "GROWTH", market: "MA", currency: "MAD", billingInterval: "ANNUAL", amountMinor: 399_000 },
  { planCode: "PRO", market: "MA", currency: "MAD", billingInterval: "MONTHLY", amountMinor: 64_900 },
  { planCode: "PRO", market: "MA", currency: "MAD", billingInterval: "ANNUAL", amountMinor: 649_000 },

  { planCode: "START", market: "INTL", currency: "USD", billingInterval: "MONTHLY", amountMinor: 4_900 },
  { planCode: "START", market: "INTL", currency: "USD", billingInterval: "ANNUAL", amountMinor: 46_800 },
  { planCode: "GROWTH", market: "INTL", currency: "USD", billingInterval: "MONTHLY", amountMinor: 9_900 },
  { planCode: "GROWTH", market: "INTL", currency: "USD", billingInterval: "ANNUAL", amountMinor: 94_800 },
  { planCode: "PRO", market: "INTL", currency: "USD", billingInterval: "MONTHLY", amountMinor: 19_900 },
  { planCode: "PRO", market: "INTL", currency: "USD", billingInterval: "ANNUAL", amountMinor: 190_800 },
] as const;

export function resolveBillingMarket(countryCode: string) {
  const normalizedCountryCode = normalizeCountryCode(countryCode);

  if (!normalizedCountryCode) {
    throw new TypeError("countryCode is required.");
  }

  if (!isSupportedCountryCode(normalizedCountryCode)) {
    throw new RangeError(`Unsupported billing country: ${normalizedCountryCode}.`);
  }

  if (normalizedCountryCode === "MA") {
    return {
      countryCode: normalizedCountryCode,
      market: "MA" as const,
      currency: "MAD" as const,
    };
  }

  return {
    countryCode: normalizedCountryCode,
    market: "INTL" as const,
    currency: "USD" as const,
  };
}

export function resolveOrganizationBillingMarket(input: {
  billingCountry: string | null;
  billingCurrency: string | null;
}) {
  const billingCountry = input.billingCountry?.trim().toUpperCase();
  const billingCurrency = input.billingCurrency?.trim().toUpperCase();

  if (!billingCountry) {
    throw new Error("Organization billing country is not configured.");
  }

  if (!billingCurrency) {
    throw new Error("Organization billing currency is not configured.");
  }

  const resolved = resolveBillingMarket(billingCountry);

  if (billingCurrency !== resolved.currency) {
    throw new Error(
      `Organization billing currency ${billingCurrency} does not match pricing policy ${resolved.currency} for ${resolved.countryCode}.`,
    );
  }

  return {
    market: resolved.market,
    currency: resolved.currency,
  };
}

export function findPricingCatalogPrice(input: {
  planCode: StandardPlanCode;
  countryCode: string;
  billingInterval: StandardBillingInterval;
}): PricingCatalogPrice | undefined {
  const market = resolveBillingMarket(input.countryCode);

  return pricingCatalogPrices.find(
    (price) =>
      price.planCode === input.planCode &&
      price.market === market.market &&
      price.currency === market.currency &&
      price.billingInterval === input.billingInterval,
  );
}

export function requirePricingCatalogPrice(input: {
  planCode: StandardPlanCode;
  countryCode: string;
  billingInterval: StandardBillingInterval;
}): PricingCatalogPrice {
  const price = findPricingCatalogPrice(input);

  if (!price) {
    throw new Error(
      `Pricing unavailable for ${input.planCode}:${input.countryCode}:${input.billingInterval}.`,
    );
  }

  return price;
}
